import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../utils/api";
import { useAuth } from "./AuthContext";
import { getGuestCart, saveGuestCart, clearGuestCart } from "../utils/cartMerge";
import { getSelectedOptionsKey, normalizeSelectedOptions } from "../utils/productOptions";
import { toast } from "react-toastify";

const CartContext = createContext();

const normalizeCartItem = (item) => {
  const product = item.product || {
    id: item.product_id,
    name: item.product_name || item.name || item.product?.name,
    sku: item.product_sku || item.sku || item.product?.sku,
    price: item.price ?? item.product?.price ?? 0,
    product_payment_method_ids: item.product_payment_method_ids || item.product?.product_payment_method_ids || [],
    product_shipping_method_ids: item.product_shipping_method_ids || item.product?.product_shipping_method_ids || [],
    status: item.status || item.product?.status,
    image: item.product_image || item.product?.image || null,
  };

  return {
    id: item.id ?? null,
    product_id: item.product_id ?? product.id,
    quantity: item.quantity,
    price: Number(item.price ?? product.price ?? 0),
    product_name: item.product_name || product.name || "Product",
    product_sku: item.product_sku || product.sku || "",
    product_image: item.product_image || product.image || null,
    product_payment_method_ids: item.product_payment_method_ids || product.product_payment_method_ids || [],
    product_shipping_method_ids: item.product_shipping_method_ids || product.product_shipping_method_ids || [],
    selected_options: normalizeSelectedOptions(item.selected_options || {}),
    status: item.status || product.status || null,
    product,
  };
};

const normalizeCartItems = (items = []) => items.map(normalizeCartItem);

const matchesGuestCartItem = (item, candidate) => {
  if (!candidate || typeof candidate !== "object") {
    return item.product_id === candidate;
  }

  return item.product_id === candidate.product_id && getSelectedOptionsKey(item.selected_options) === getSelectedOptionsKey(candidate.selected_options);
};

const resolveCartItemIdentifier = (itemOrId, authenticated) => {
  if (itemOrId && typeof itemOrId === "object") {
    return authenticated ? itemOrId.id ?? itemOrId.product_id : itemOrId.product_id ?? itemOrId.id;
  }

  return itemOrId;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  // Load cart on mount and when user changes (but only after auth is loaded)
  useEffect(() => {
    if (authLoading) return; // Wait for auth to finish loading

    const loadCart = async () => {
      if (user && isAuthenticated()) {
        // Load user cart from API
        try {
          const response = await api.get("/cart");
          setCart(normalizeCartItems(response.data.items || []));
        } catch (error) {
          // If cart endpoint doesn't exist yet, use empty array
          if (error.response?.status === 404) {
            setCart([]);
          } else {
            console.error("Error fetching cart:", error);
            setCart([]);
          }
        }
      } else {
        // Load guest cart from localStorage
        const guestCart = normalizeCartItems(getGuestCart());
        setCart(guestCart);
      }
    };

    loadCart();
  }, [user, isAuthenticated, authLoading]);

  const addToCart = async (product, quantity = 1, selectedOptions = {}) => {
    setLoading(true);
    const normalizedSelectedOptions = normalizeSelectedOptions(selectedOptions);

    try {
      if (isAuthenticated()) {
        // Add to database cart
        const response = await api.post("/cart", {
          product_id: product.id,
          quantity,
          selected_options: normalizedSelectedOptions,
        });
        setCart(normalizeCartItems(response.data.items));
        toast.success("Added to cart");
      } else {
        // Add to localStorage cart
        const existingIndex = cart.findIndex((item) => item.product_id === product.id && getSelectedOptionsKey(item.selected_options) === getSelectedOptionsKey(normalizedSelectedOptions));
        let newCart;

        if (existingIndex >= 0) {
          newCart = [...cart];
          newCart[existingIndex].quantity += quantity;
        } else {
          newCart = [...cart, { product_id: product.id, product, quantity, selected_options: normalizedSelectedOptions }];
        }

        const normalizedCart = normalizeCartItems(newCart);
        setCart(normalizedCart);
        saveGuestCart(normalizedCart);
        toast.success("Added to cart");
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to add to cart");
      console.error("Add to cart error:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemOrId, quantity) => {
    if (quantity < 1) return;

    setLoading(true);

    try {
      const authenticated = isAuthenticated();
      const itemId = resolveCartItemIdentifier(itemOrId, authenticated);

      if (!itemId) {
        throw new Error("Cart item identifier is missing");
      }

      if (authenticated) {
        const response = await api.put(`/cart/${itemId}`, { quantity });
        setCart(normalizeCartItems(response.data.items));
      } else {
        const newCart = cart.map((item) => (matchesGuestCartItem(item, itemOrId) ? { ...item, quantity } : item));
        const normalizedCart = normalizeCartItems(newCart);
        setCart(normalizedCart);
        saveGuestCart(normalizedCart);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to update quantity");
      console.error("Update quantity error:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemOrId) => {
    setLoading(true);

    try {
      const authenticated = isAuthenticated();
      const itemId = resolveCartItemIdentifier(itemOrId, authenticated);

      if (!itemId) {
        throw new Error("Cart item identifier is missing");
      }

      if (authenticated) {
        const response = await api.delete(`/cart/${itemId}`);
        setCart(normalizeCartItems(response.data.items));
        toast.success("Removed from cart");
      } else {
        const newCart = cart.filter((item) => !matchesGuestCartItem(item, itemOrId));
        const normalizedCart = normalizeCartItems(newCart);
        setCart(normalizedCart);
        saveGuestCart(normalizedCart);
        toast.success("Removed from cart");
      }
    } catch (error) {
      toast.error("Failed to remove item");
      console.error("Remove from cart error:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      if (isAuthenticated()) {
        await api.delete("/cart");
        setCart([]);
      } else {
        setCart([]);
        clearGuestCart();
      }
    } catch (error) {
      console.error("Clear cart error:", error);
    }
  };

  const mergeGuestCart = async () => {
    const guestCart = getGuestCart();
    if (guestCart.length === 0) return;

    try {
      // Send guest cart to API for merging
      const response = await api.post("/cart/merge", { guestCart });
      setCart(normalizeCartItems(response.data.items));
      clearGuestCart();
      toast.success(`Merged ${guestCart.length} items from your cart`);
    } catch (error) {
      // If cart merge endpoint doesn't exist yet, just use guest cart as is
      if (error.response?.status === 404) {
        console.log("Cart merge endpoint not yet implemented - keeping guest cart");
      } else {
        console.error("Cart merge error:", error);
      }
    }
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const price = Number(item.price ?? item.product?.price ?? 0);
      return total + price * item.quantity;
    }, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const refreshCart = async () => {
    if (user && isAuthenticated()) {
      try {
        const response = await api.get("/cart");
        setCart(normalizeCartItems(response.data.items || []));
      } catch (error) {
        if (error.response?.status === 404) {
          setCart([]);
        }
      }
    } else {
      const guestCart = normalizeCartItems(getGuestCart());
      setCart(guestCart);
    }
  };

  const value = {
    cart,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    mergeGuestCart,
    getCartTotal,
    getCartCount,
    refreshCart,
    getCart: refreshCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
